/**
 * PD-35962 — SE3 (HomepageV3 + Explore) experience-driven hero slots.
 *
 * Maps the existing Homepage V2 placement `featured_offering` content
 * (FeaturedOfferingV2 / FeaturedOfferingConfig shape) onto the SE3 hero-slot
 * UI shape, and selects the slot matching the user's `getSe2Experience(...)`
 * bucket ('default' | 'retail').
 *
 * Reuses the existing placement content model — no new Strapi content type is
 * introduced here. The discover-side mapping is:
 *   default → accredited placement's featured_offering
 *   retail  → retail placement's featured_offering
 *
 * The CMS may not yet expose SE3-specific hero copy/imagery (see PR body
 * "CMS dependency"). When a slot is absent/unpublished the selector returns
 * null and the consumer falls back to the hardcoded HomepageV3 demoData hero.
 */
import { cmsCDNPath } from 'utils/helpers';
import type {
  FeaturedOfferingV2,
  FeaturedOfferingConfig,
  Se3HeroExperience,
  Se3HeroSlot,
  Se3HeroSlots,
} from 'typescriptUtils/types/homepageV2Config';

type StrapiImageLike =
  | {
      url?: string;
      hash?: string;
      ext?: string;
      data?: { attributes?: { url?: string; hash?: string; ext?: string } };
    }
  | undefined;

/** Resolve a Strapi v4/v5 image field to an absolute URL (shared helper). */
export const resolveStrapiImageUrl = (imageData: StrapiImageLike): string | undefined => {
  if (!imageData) return undefined;
  const attrs = imageData.data?.attributes;
  const url = attrs?.url || imageData.url;
  const hash = attrs?.hash || imageData.hash;
  const ext = attrs?.ext || imageData.ext;
  if (url && url.trim()) {
    const normalized = url.trim();
    if (/^https?:\/\//i.test(normalized)) return normalized;
    if (normalized.startsWith('/')) return `${cmsCDNPath}${normalized}`;
    return `${cmsCDNPath}/${normalized}`;
  }
  if (hash && hash.trim() && ext && ext.trim()) {
    return `${cmsCDNPath}/images/origin/${hash}${ext}`;
  }
  return undefined;
};

type FeaturedOffering = FeaturedOfferingV2 | FeaturedOfferingConfig;

const HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

export const toPlainText = (value?: string): string | undefined => {
  if (!value) return undefined;

  const decoded = value.replace(/&(#(?:x[\da-f]+|\d+)|[a-z]+);/gi, (entity, code: string) => {
    if (code.startsWith('#')) {
      const isHex = code[1]?.toLowerCase() === 'x';
      const codePoint = Number.parseInt(code.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    return HTML_ENTITIES[code.toLowerCase()] ?? entity;
  });
  const text = decoded
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(?:div|li|p)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return text || undefined;
};

/**
 * Build a resolved SE3 hero slot from a placement's featured_offering.
 * Returns null when the source has no offering_slug (treated as "no content"),
 * so the consumer can fall back to demoData.
 */
export const buildSe3HeroSlot = (
  featured: FeaturedOffering | null | undefined,
  experience: Se3HeroExperience,
  sourceExperience: 'retail' | 'accredited' | 'qp',
): Se3HeroSlot | null => {
  if (!featured || !featured.offering_slug) return null;
  return {
    experience,
    sourceExperience,
    offeringSlug: featured.offering_slug,
    title: featured.custom_title || undefined,
    tagline: featured.custom_tagline || undefined,
    category: featured.custom_category || undefined,
    heroImageDesktop: resolveStrapiImageUrl(featured.hero_image_desktop),
    heroImageMobile: resolveStrapiImageUrl(featured.hero_image_mobile),
    ctaLabel: toPlainText(featured.button_text),
    ctaQueryString: featured.query_string || undefined,
    legalText: featured.legal_text || undefined,
    badgeText: featured.badge_text || undefined,
  };
};

/**
 * Pick the SE3 hero slot matching the experience bucket. Falls back to the
 * `default` slot when the requested experience slot is absent — and the caller
 * falls back to demoData when even `default` is null.
 */
export const selectSe3HeroSlot = (
  slots: Se3HeroSlots | null | undefined,
  experience: Se3HeroExperience,
): Se3HeroSlot | null => {
  if (!slots) return null;
  if (experience === 'retail') {
    return slots.retail || slots.default || null;
  }
  return slots.default || null;
};
